import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { usePersistedBool } from "@/lib/collassabile";
import { StatoVuoto, IllustrazioneTuttoOk } from "@/components/Illustrazioni";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AssegnaCompitoDialog from "@/components/AssegnaCompitoDialog";
import { Sunrise, CheckCircle2, Circle, Clock, AlertTriangle, PauseCircle, Mail, Plus, Trash2, Loader2, BellRing, ChevronDown, ChevronRight } from "lucide-react";
import { eur } from "@/lib/format";

const ora = (iso) => (iso && iso.length > 11 ? new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "tutto il g.");

export default function MioGiorno({ senzaCornice }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [assegna, setAssegna] = useState(false);
  const [bozza, setBozza] = useState(false);
  const { data: g, isLoading } = useQuery({
    queryKey: ["mio-giorno"],
    queryFn: async () => (await api.get("/mio-giorno")).data,
    refetchInterval: 5 * 60 * 1000,
  });

  const aggiorna = () => { qc.invalidateQueries({ queryKey: ["mio-giorno"] }); qc.invalidateQueries({ queryKey: ["compiti"] }); };

  const spunta = async (c) => {
    try {
      await api.patch(`/compiti/${c.id}`, { stato: c.stato === "fatto" ? "aperto" : "fatto" });
      aggiorna();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const elimina = async (c) => {
    if (!window.confirm(`Eliminare "${c.titolo}"?`)) return;
    try { await api.delete(`/compiti/${c.id}`); toast.success("Compito eliminato"); aggiorna(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const creaBozza = async () => {
    setBozza(true);
    try {
      const { data } = await api.post("/mio-giorno/bozza");
      toast.success("Bozza email creata in Gmail");
      if (data.draft_link) window.open(data.draft_link, "_blank");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nella creazione della bozza"); }
    finally { setBozza(false); }
  };

  const [apertoLocale, setAperto] = usePersistedBool("home_mio_giorno_open", true);
  const aperto = senzaCornice ? true : apertoLocale;
  const acconti = g?.acconti_scaduti || [];
  const totale = (g?.compiti?.length || 0) + (g?.appuntamenti?.length || 0)
    + (g?.documenti_mancanti?.length || 0) + (g?.pratiche_ferme?.length || 0) + acconti.length;

  const azioni = (
    <>
      {can("gestione_utenti") && (
        <button data-testid="assegna-compito-btn" onClick={() => setAssegna(true)} className="btn btn-o btn-piccolo">
          <Plus className="w-3.5 h-3.5" /> Assegna compito
        </button>
      )}
      <button data-testid="mio-giorno-bozza" onClick={creaBozza} disabled={bozza} className="btn btn-o btn-piccolo">
        {bozza ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Bozza email
      </button>
    </>
  );

  return (
    <div className={senzaCornice ? "" : "bg-white border border-[#1A2B4A]/10 rounded-lg shadow-sm mb-5"} data-testid="mio-giorno">
      {senzaCornice ? (
        <div className="flex flex-wrap items-center justify-between gap-2.5 px-4 pt-3.5">
          <span className="eti" data-testid="mio-giorno-count">
            {isLoading ? "sto preparando il riepilogo" : totale === 0 ? "tutto in ordine" : `${totale} voci`}
          </span>
          <span className="flex flex-wrap gap-2.5">{azioni}</span>
        </div>
      ) : (
      <div className="flex items-center justify-between gap-2 flex-wrap px-4 py-2.5 border-b border-[#1A2B4A]/8">
        <button data-testid="mio-giorno-toggle" onClick={() => setAperto(!aperto)} className="flex items-center gap-2 text-left">
          {aperto ? <ChevronDown className="w-4 h-4 text-[#1A2B4A]" /> : <ChevronRight className="w-4 h-4 text-[#1A2B4A]" />}
          <Sunrise className="w-4 h-4 text-[#C9A84C]" />
          <h2 className="font-serif text-lg text-[#1A2B4A]">I miei compiti di oggi</h2>
        </button>
        {aperto && <div className="flex items-center gap-2">{azioni}</div>}
      </div>
      )}

      {aperto && (
      <>

      {acconti.length > 0 && (
        <div data-testid="mg-acconti-scaduti" className="mx-4 mt-3 rounded-md border border-[#9b2c2c]/25 bg-[#fdf3f3] px-3 py-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#9b2c2c] flex items-center gap-1.5 mb-1.5">
            <BellRing className="w-3.5 h-3.5" /> Acconti concordati e non incassati
          </p>
          <ul className="space-y-1">
            {acconti.map((x, i) => (
              <li key={i} data-testid={`mg-acconto-${i}`} className="text-sm">
                <Link to={`/pratiche/${x.pratica_id}`} className="hover:text-[#C9A84C]">
                  <span className="text-[#1A2B4A] font-medium">{x.cliente}</span>
                  <span className="text-[#9b2c2c]"> · mancano {eur(x.mancante)}</span>
                  <span className="block text-[11px] text-[#8a93a6]">
                    {x.descrizione} di {eur(x.importo)} atteso il {x.data} · {x.giorni} giorni di ritardo
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isLoading ? (
        <div className="p-4 text-sm text-[#4a5568]">Sto preparando il riepilogo…</div>
      ) : totale === 0 ? (
        <StatoVuoto testid="mg-vuoto" illustrazione={IllustrazioneTuttoOk}
          titolo="Tutto in ordine, nessuna urgenza per oggi"
          testo="Nessun appuntamento, nessun compito aperto e nessun documento obbligatorio mancante." />
      ) : (
        <div className="p-4 grid md:grid-cols-2 gap-x-5 gap-y-4">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6] mb-1.5">Appuntamenti di oggi</p>
            {g.appuntamenti.length === 0 ? <p className="text-sm text-[#8a93a6]">Nessun appuntamento.</p> : (
              <ul className="space-y-1.5" data-testid="mg-appuntamenti">
                {g.appuntamenti.map((a, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#1A2B4A]">
                    <Clock className="w-3.5 h-3.5 text-[#4a5568] mt-0.5 shrink-0" />
                    <span className="font-medium w-12 shrink-0">{ora(a.start)}</span>
                    {a.privato ? (
                      <span className="text-[#4a5568] italic break-words">Occupato</span>
                    ) : (
                      <a href={a.htmlLink} target="_blank" rel="noreferrer" className="hover:text-[#C9A84C] break-words">{a.titolo}</a>
                    )}
                    {a.calendario !== "mio" && a.calendario_label && (
                      <span className="text-[9.5px] uppercase rounded-sm px-1 border"
                        style={{ color: a.calendario_colore, borderColor: `${a.calendario_colore}55`, background: `${a.calendario_colore}12` }}>
                        {a.calendario_label}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6] mb-1.5">Da fare</p>
            {g.compiti.length === 0 ? <p className="text-sm text-[#8a93a6]">Nessun compito aperto.</p> : (
              <ul className="space-y-1.5" data-testid="mg-compiti">
                {g.compiti.map((c) => (
                  <li key={c.id} data-testid={`mg-compito-${c.id}`} className="flex items-start gap-2 group">
                    <button data-testid={`mg-compito-done-${c.id}`} onClick={() => spunta(c)} title="Segna come fatto"
                      className="mt-0.5 text-[#4a5568] hover:text-[#276749]">
                      {c.stato === "fatto" ? <CheckCircle2 className="w-4 h-4 text-[#276749]" /> : <Circle className="w-4 h-4" />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <span className="text-sm text-[#1A2B4A] break-words">{c.titolo}</span>
                      <span className="block text-[11px] text-[#8a93a6]">
                        {c.tipo === "appuntamento" ? "Appuntamento" : c.tipo === "promemoria" ? "Promemoria" : "To-do"}
                        {c.scadenza ? ` · ${c.scadenza.slice(0, 10).split("-").reverse().join("/")}` : ""}
                        {c.in_ritardo ? " · in ritardo" : c.oggi ? " · oggi" : ""}
                        {c.assegnato_da_nome ? ` · da ${c.assegnato_da_nome}` : ""}
                      </span>
                    </div>
                    <button data-testid={`mg-compito-del-${c.id}`} onClick={() => elimina(c)}
                      className="opacity-0 group-hover:opacity-100 text-[#9b2c2c]/70 hover:text-[#9b2c2c]"><Trash2 className="w-3.5 h-3.5" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6] mb-1.5">Documenti obbligatori mancanti</p>
            {g.documenti_mancanti.length === 0 ? <p className="text-sm text-[#8a93a6]">Nessuno.</p> : (
              <ul className="space-y-1.5" data-testid="mg-documenti">
                {g.documenti_mancanti.map((d) => (
                  <li key={d.pratica_id} className="flex items-start gap-2 text-sm">
                    <AlertTriangle className="w-3.5 h-3.5 text-[#9b2c2c] mt-0.5 shrink-0" />
                    <Link to={`/pratiche/${d.pratica_id}`} className="min-w-0 hover:text-[#C9A84C]">
                      <span className="text-[#1A2B4A]">{d.cliente}</span>
                      {d.tipo && (
                        <span data-testid={`mg-doc-pratica-${d.pratica_id}`} className="text-[11px] text-[#4a5568]">
                          {" — "}{d.tipo}
                        </span>
                      )}
                      {(d.dettagli || []).length > 0 ? (
                        <span className="block text-[11px] text-[#8a93a6]" data-testid={`mg-doc-${d.pratica_id}`}>
                          {d.dettagli.map((x) => `${x.nome}${x.motivo ? ` (${x.motivo})` : ""}${x.da_giorni ? ` · da ${x.da_giorni} gg` : ""}`).join(" · ")}
                        </span>
                      ) : (
                        <span className="block text-[11px] text-[#8a93a6]">{d.documenti.join(" · ")}</span>
                      )}
                      {d.in_ritardo && (
                        <span data-testid={`mg-doc-ritardo-${d.pratica_id}`} className="block text-[11px] text-[#9b2c2c]">
                          in attesa da più di {d.giorni_alert} giorni: da sollecitare
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6] mb-1.5">Pratiche ferme da oltre 30 giorni</p>
            {g.pratiche_ferme.length === 0 ? <p className="text-sm text-[#8a93a6]">Nessuna.</p> : (
              <ul className="space-y-1.5" data-testid="mg-ferme">
                {g.pratiche_ferme.map((p) => (
                  <li key={p.pratica_id} className="flex items-start gap-2 text-sm">
                    <PauseCircle className="w-3.5 h-3.5 text-[#C9A84C] mt-0.5 shrink-0" />
                    <Link to={`/pratiche/${p.pratica_id}`} className="min-w-0 hover:text-[#C9A84C]">
                      <span className="text-[#1A2B4A]">{p.cliente}</span>
                      <span className="block text-[11px] text-[#8a93a6]">{p.tipo} · dal {p.ultimo_aggiornamento?.split("-").reverse().join("/")}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      </>
      )}

      {assegna && <AssegnaCompitoDialog onClose={() => setAssegna(false)} onSaved={aggiorna} />}
    </div>
  );
}
